import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Empty,
  Popconfirm,
  Skeleton,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { KeyOutlined, PlusOutlined, ReloadOutlined, SwapOutlined } from "@ant-design/icons";
import api from "src/services/api";
import { useLanguage } from "src/providers/LanguageProvider";
import { RESOLVER_API_URL, RESOLVER_PRODUCTS, RESOLVER_SWAGGER_URL } from "./resolverProducts";
import { showResolverApiKey } from "./showResolverApiKey";

const { Title, Text, Paragraph } = Typography;

type ResolverOverview = {
  licenseId: number;
  product: "VIN" | "PLATE";
  subscription: {
    id: string;
    plan: string;
    status: string;
    validFrom: string | null;
    expiresAt: string | null;
  } | null;
  queriesThisMonth: number;
  keys: {
    id: string;
    keyPrefix: string;
    status: string;
    createdAt: string | null;
  }[];
  issuedApiKey: string | null;
};

const formatDate = (value: string | null) => (value ? new Date(value).toLocaleDateString() : "-");

const ResolverPage: React.FC = () => {
  const { product: slug } = useParams();
  const { t } = useLanguage();
  const { message, modal } = App.useApp();

  const meta = RESOLVER_PRODUCTS.find((p) => p.slug === slug) ?? RESOLVER_PRODUCTS[0];

  const [data, setData] = useState<ResolverOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res: any = await api.get(`/licenses/resolver/${meta.slug}`);
      const overview: ResolverOverview = res.data;
      setData(overview);
      if (overview.issuedApiKey) showResolverApiKey(modal, overview.issuedApiKey, t);
    } catch (err: any) {
      setError(t(err?.response?.data?.message || "ERROR_GENERIC"));
    } finally {
      setLoading(false);
    }
  }, [meta.slug, modal, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleKeyError = (err: any) => {
    const code = err?.response?.data?.details?.error_code || err?.response?.data?.message;
    message.error(t(code || "ERROR_GENERIC"));
  };

  const createKey = async () => {
    setBusyKey("new");
    try {
      const res: any = await api.post(`/licenses/resolver/${meta.slug}/keys`);
      showResolverApiKey(modal, res.data.apiKey, t);
      await load();
    } catch (err) {
      handleKeyError(err);
    } finally {
      setBusyKey(null);
    }
  };

  const replaceKey = async (keyId: string) => {
    setBusyKey(keyId);
    try {
      const res: any = await api.post(`/licenses/resolver/${meta.slug}/keys/${keyId}/replace`);
      showResolverApiKey(modal, res.data.apiKey, t);
      await load();
    } catch (err) {
      handleKeyError(err);
    } finally {
      setBusyKey(null);
    }
  };

  const current = data;
  const activeKeys = data?.keys ?? [];

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Space style={{ width: "100%", justifyContent: "space-between" }} wrap>
        <Title level={3} style={{ margin: 0 }}>
          {t(meta.menuKey, meta.name)}
        </Title>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
          {t("BTN_REFRESH", "Refresh")}
        </Button>
      </Space>

      {error && <Alert type="error" showIcon message={error} />}

      <Card className="card" title={t("RESOLVER_SUBSCRIPTION", "Subscription")}>
        {loading && !data ? (
          <Skeleton active paragraph={{ rows: 3 }} />
        ) : current?.subscription ? (
          <Descriptions column={{ xs: 1, md: 2 }} size="small">
            <Descriptions.Item label={t("RESOLVER_STATUS", "Status")}>
              <Tag color={current.subscription.status === "ACTIVE" ? "green" : "orange"}>
                {current.subscription.status === "ACTIVE"
                  ? t("RESOLVER_STATUS_ACTIVE", "Active")
                  : current.subscription.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t("RESOLVER_VALID_FROM", "Active since")}>
              {formatDate(current.subscription.validFrom)}
            </Descriptions.Item>
            <Descriptions.Item label={t("RESOLVER_QUERIES_THIS_MONTH", "Queries this month")}>
              {current.queriesThisMonth}
            </Descriptions.Item>
            <Descriptions.Item label={t("RESOLVER_API_URL", "API address")}>
              <Text copyable>{RESOLVER_API_URL}</Text>
            </Descriptions.Item>
            <Descriptions.Item label={t("RESOLVER_API_DOCS", "API documentation (Swagger)")}>
              <Typography.Link href={RESOLVER_SWAGGER_URL} target="_blank" rel="noopener noreferrer" copyable>
                {RESOLVER_SWAGGER_URL}
              </Typography.Link>
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <Empty description={t("RESOLVER_NO_SUBSCRIPTION", "You have no active subscription for this product.")} />
        )}
      </Card>

      <Card
        className="card"
        title={
          <Space>
            <KeyOutlined />
            {t("RESOLVER_API_KEYS", "API keys")}
          </Space>
        }
        extra={
          !loading && data && activeKeys.length === 0 && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={createKey}
              loading={busyKey === "new"}
              disabled={!current?.subscription}
            >
              {t("RESOLVER_CREATE_KEY", "Create key")}
            </Button>
          )
        }
      >
        <Paragraph type="secondary">
          {t(
            "RESOLVER_KEYS_INFO",
            "This product has its own API key. For security the full key is only shown when it is created; here you only see its first characters. If you lose it, issue a new key: the old one stops working."
          )}
        </Paragraph>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={activeKeys}
          pagination={false}
          scroll={{ x: true }}
          locale={{ emptyText: t("RESOLVER_NO_KEYS", "No API key yet.") }}
          columns={[
            {
              title: t("RESOLVER_KEY", "Key"),
              dataIndex: "keyPrefix",
              render: (v: string) => <Text code>{v}…</Text>,
            },
            {
              title: t("RESOLVER_KEY_CREATED", "Created"),
              dataIndex: "createdAt",
              render: formatDate,
            },
            {
              title: "",
              key: "actions",
              align: "right" as const,
              render: (_: unknown, row) => (
                <Popconfirm
                  title={t("RESOLVER_REPLACE_TITLE", "Issue a new key?")}
                  description={t(
                    "RESOLVER_REPLACE_DESC",
                    "The current key stops working within 30 seconds and a new key is shown once."
                  )}
                  okText={t("YES")}
                  cancelText={t("NO")}
                  onConfirm={() => replaceKey(row.id)}
                >
                  <Button icon={<SwapOutlined />} loading={busyKey === row.id}>
                    {t("RESOLVER_REPLACE_KEY", "Issue new key")}
                  </Button>
                </Popconfirm>
              ),
            },
          ]}
        />
      </Card>

      <Card className="card" title={t("RESOLVER_USAGE_EXAMPLE", "Example request")}>
        <Paragraph type="secondary">
          {t("RESOLVER_USAGE_INFO", "Send the key only from your server in the X-API-Key header, never from a browser or app.")}
        </Paragraph>
        <pre style={{ overflowX: "auto", margin: 0 }}>
          {`curl -X POST "${RESOLVER_API_URL}/v1/vehicle/resolve" \\
  -H "X-API-Key: vrk_..." \\
  -H "Content-Type: application/json" \\
  -d '${
    meta.product === "VIN"
      ? '{"identifiers": [{"type": "vin", "value": "WVWZZZ1JZXW000001"}]}'
      : '{"identifiers": [{"type": "plate", "value": "AB-123-C", "country": "NL"}]}'
  }'`}
        </pre>
      </Card>

    </Space>
  );
};

export default ResolverPage;
